import * as React from 'react'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Loader2,
  CheckCircle2,
  Clock,
  XCircle,
  Pause,
  Save,
  User,
  MessageSquare,
  Phone,
  Image,
  FileText as FileIcon,
  Video,
} from 'lucide-react'
import { useWATemplate, useWATemplatePreview, useUpdateWATemplate } from '@/hooks/useWATemplates'
import type { WATemplateStatus, WATemplateCategory } from '@/lib/api-client'

interface TemplatePreviewProps {
  templateId: number
  patientId?: number
  onClose?: () => void
}

// Available variable mappings
const VARIABLE_OPTIONS = [
  { value: 'patient.name', label: 'Patient Name' },
  { value: 'patient.pno', label: 'Patient Number (PNO)' },
  { value: 'patient.phone', label: 'Patient Phone' },
  { value: 'patient.gender', label: 'Gender' },
  { value: 'branch.name', label: 'Branch Name' },
  { value: 'branch.shortName', label: 'Branch Code' },
  { value: 'appointment.date', label: 'Appointment Date' },
  { value: 'appointment.time', label: 'Appointment Time' },
  { value: 'custom', label: 'Custom Value' },
]

export function TemplatePreview({
  templateId,
  patientId,
  onClose,
}: TemplatePreviewProps) {
  const { data: template, isPending, isError } = useWATemplate(templateId)
  const { data: preview, isPending: previewPending } = useWATemplatePreview(templateId, patientId)
  const updateMutation = useUpdateWATemplate()

  // Local state for variable mapping
  const [variableMapping, setVariableMapping] = React.useState<Record<string, string>>({})
  const [hasChanges, setHasChanges] = React.useState(false)

  // Initialize mapping when template loads
  React.useEffect(() => {
    if (template?.variableMapping) {
      setVariableMapping(template.variableMapping)
    }
  }, [template?.variableMapping])

  // Count variables
  const variableCount = template ? countVariables(template.bodyContent) : 0

  // Handle mapping change
  const handleMappingChange = (varNum: string, value: string) => {
    setVariableMapping(prev => ({
      ...prev,
      [varNum]: value,
    }))
    setHasChanges(true)
  }

  // Save mapping
  const handleSave = () => {
    updateMutation.mutate(
      { id: templateId, data: { variableMapping } },
      {
        onSuccess: () => {
          setHasChanges(false)
        },
      }
    )
  }

  if (isPending) {
    return (
      <div className="space-y-4 p-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    )
  }

  if (isError || !template) {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <XCircle className="w-10 h-10 text-destructive mb-3" />
        <p className="text-sm text-text-muted">Failed to load template</p>
      </div>
    )
  }

  const statusConfig = getStatusConfig(template.status)

  return (
    <div className="space-y-4">
      {/* Template Info */}
      <div className="flex items-center gap-2">
        <Badge
          variant="outline"
          className={cn('text-[10px] px-1.5 py-0 h-5', statusConfig.className)}
        >
          {statusConfig.icon}
          <span className="ml-1">{statusConfig.label}</span>
        </Badge>
        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-5">
          {getCategoryLabel(template.category)}
        </Badge>
        <span className="text-[10px] text-text-muted ml-auto">
          {template.language.toUpperCase()}
        </span>
      </div>

      {/* Message Preview */}
      <div className="space-y-2">
        <Label className="text-xs text-text-muted">Message Preview</Label>
        <div className="p-4 rounded-lg bg-muted/30 border border-border">
          {/* Header - media or text */}
          {template.headerType && template.headerType !== 'text' ? (
            <div className="mb-3">
              {template.headerType === 'image' && template.headerContent ? (
                <img
                  src={template.headerContent}
                  alt="Template header"
                  className="w-full h-40 object-cover rounded-lg"
                />
              ) : (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50 text-text-muted">
                  {template.headerType === 'video' ? (
                    <Video className="w-5 h-5" />
                  ) : (
                    <FileIcon className="w-5 h-5" />
                  )}
                  <span className="text-xs capitalize">{template.headerType} header</span>
                </div>
              )}
            </div>
          ) : template.headerContent ? (
            <p className="font-semibold text-sm text-text-dark mb-2">
              {preview?.preview.header || template.headerContent}
            </p>
          ) : null}

          {/* Body */}
          <p className="text-sm text-text-dark whitespace-pre-wrap leading-relaxed">
            {preview?.preview.body || highlightVariables(template.bodyContent)}
          </p>

          {/* Footer */}
          {template.footerContent && (
            <p className="text-xs text-text-muted mt-2 pt-2 border-t border-border/50">
              {preview?.preview.footer || template.footerContent}
            </p>
          )}

          {/* Buttons */}
          {template.buttonContent && Array.isArray(template.buttonContent) && template.buttonContent.length > 0 && (
            <div className="mt-3 pt-3 border-t border-border/50 space-y-2">
              {(template.buttonContent as Array<{ text?: string; type?: string }>).map((btn, idx) => (
                <div
                  key={idx}
                  className="w-full p-2 text-center text-xs font-medium text-info bg-info/10 rounded"
                >
                  {btn.text || `Button ${idx + 1}`}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Unresolved Variables Warning */}
      {preview?.variables.unresolved && preview.variables.unresolved.length > 0 && (
        <div className="p-3 rounded-lg bg-warning/10 border border-warning/20">
          <p className="text-xs text-warning">
            Unmapped variables: {preview.variables.unresolved.join(', ')}
          </p>
        </div>
      )}

      <div className="h-px bg-border my-2" />

      {/* Variable Mapping */}
      {variableCount > 0 && (
        <div className="space-y-3">
          <Label className="text-xs text-text-muted flex items-center gap-1">
            <MessageSquare className="w-3 h-3" />
            Variable Mapping ({variableCount})
          </Label>

          <div className="space-y-2">
            {Array.from({ length: variableCount }, (_, i) => i + 1).map((num) => (
              <div key={num} className="flex items-center gap-2">
                <span className="w-12 text-xs text-text-muted font-mono">
                  {`{{${num}}}`}
                </span>
                <Select
                  value={variableMapping[String(num)] || ''}
                  onValueChange={(value) => handleMappingChange(String(num), value)}
                >
                  <SelectTrigger className="flex-1 h-8 text-xs">
                    <SelectValue placeholder="Select value..." />
                  </SelectTrigger>
                  <SelectContent>
                    {VARIABLE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value} className="text-xs">
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="flex items-center gap-4 text-[11px] text-text-muted pt-2">
        <span className="flex items-center gap-1">
          <MessageSquare className="w-3 h-3" />
          {template.totalSent.toLocaleString()} sent
        </span>
        <span className="flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3" />
          {template.totalDelivered.toLocaleString()} delivered
        </span>
        <span className="flex items-center gap-1">
          <User className="w-3 h-3" />
          {template.totalRead.toLocaleString()} read
        </span>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 pt-2">
        <Button variant="outline" size="sm" onClick={onClose}>
          Close
        </Button>
        {hasChanges && (
          <Button
            size="sm"
            onClick={handleSave}
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save
          </Button>
        )}
      </div>
    </div>
  )
}

// Helper functions

function getStatusConfig(status: WATemplateStatus): {
  label: string
  icon: React.ReactNode
  className: string
} {
  switch (status) {
    case 'approved':
      return {
        label: 'Approved',
        icon: <CheckCircle2 className="w-3 h-3" />,
        className: 'text-success border-success/30 bg-success/5',
      }
    case 'pending':
      return {
        label: 'Pending',
        icon: <Clock className="w-3 h-3" />,
        className: 'text-warning border-warning/30 bg-warning/5',
      }
    case 'rejected':
      return {
        label: 'Rejected',
        icon: <XCircle className="w-3 h-3" />,
        className: 'text-destructive border-destructive/30 bg-destructive/5',
      }
    case 'paused':
      return {
        label: 'Paused',
        icon: <Pause className="w-3 h-3" />,
        className: 'text-text-muted border-border bg-muted/20',
      }
    default:
      return {
        label: status,
        icon: null,
        className: '',
      }
  }
}

function getCategoryLabel(category: WATemplateCategory): string {
  switch (category) {
    case 'MARKETING':
      return 'Marketing'
    case 'UTILITY':
      return 'Utility'
    case 'AUTHENTICATION':
      return 'Authentication'
    default:
      return category
  }
}

function countVariables(text: string): number {
  const matches = text.match(/\{\{(\d+)\}\}/g)
  if (!matches) return 0
  const numbers = matches.map(m => parseInt(m.replace(/\D/g, ''), 10))
  return Math.max(...numbers)
}

function highlightVariables(text: string): React.ReactNode {
  const parts = text.split(/(\{\{\d+\}\})/g)
  return parts.map((part, index) => {
    if (/\{\{\d+\}\}/.test(part)) {
      return (
        <span key={index} className="px-1 py-0.5 rounded bg-info/10 text-info font-mono text-xs">
          {part}
        </span>
      )
    }
    return part
  })
}
